/**
 * Bundled default content + site defaults.
 *
 * IMPORTANT: This mirrors the server's DEFAULT_CONTENT (server.py) and
 * SiteSettings defaults. The frontend uses these as fallbacks so the site
 * always renders correctly even when /api/content or /api/site fetches
 * fail (slow network, ad blockers, transient CDN errors, etc.).
 *
 * Live CMS edits still win — API responses are deep-merged on top of these.
 */

import { DECK_IMG, HERO_IMG, SERVICE_IMG } from "./api";

export const DEFAULT_CONTENT = {
  global: {
    header_tagline: "Since Quality Counts",
    footer_blurb:
      "Family-owned home improvement & construction. Lifetime labor warranty on every project.",
    header_cta_label: "Free Estimate",
    trust_badges: ["Licensed", "Insured", "Lifetime Warranty", "Local"],
  },
  cta_band: {
    overline: "Ready when you are",
    title_line_1: "Schedule your",
    title_line_2: "free estimate today",
    body:
      "One call. Real humans. No gimmicks. We'll walk the job with you and send you a clean, honest quote.",
    call_button_prefix: "Call",
    quote_button: "Request a Quote",
  },
  home: {
    hero_overline_suffix: "Est. ATC Home Pro's",
    hero_title_line_1: "When quality",
    hero_title_line_2: "counts… count",
    hero_title_line_3_red: "on us.",
    hero_body:
      "High-quality home improvement & construction inside and out — decks, kitchens, baths, additions, roofing and siding. Family-owned. Backed by a lifetime labor warranty.",
    hero_cta_primary: "Get a free estimate",
    hero_cta_secondary: "Explore services",
    stats: [
      { value: "35+", label: "Years Combined" },
      { value: "1hr", label: "Service Radius" },
      { value: "∞", label: "Labor Warranty" },
      { value: "100%", label: "Family-Owned" },
    ],
    intro_overline: "About ATC",
    intro_title_line_1: "Houses into",
    intro_title_line_2: "dream homes.",
    intro_body_1:
      "ATC Home Pro's is your trusted home-improvement company — proudly serving Southern York County and Northern Maryland within a one-hour radius. We're a family-owned crew of licensed contractors with decades of combined experience in renovation, remodeling, and repair.",
    intro_body_2:
      "Whether it's a custom deck, a full kitchen, a new roof, or an emergency repair — we deliver quality that stands the test of time.",
    why_overline: "Why ATC",
    why_title_line_1: "The difference",
    why_title_line_2: "is in the details.",
    why_subtitle:
      "Six reasons homeowners across York County keep calling us back — and sending their neighbors.",
    benefits: [
      {
        icon: "ShieldCheck",
        title: "Lifetime Labor Warranty",
        body: "Every project backed by our commitment to quality workmanship.",
      },
      {
        icon: "House",
        title: "Full Showroom Design Center",
        body: "Visit our Loganville showroom to explore materials and designs.",
      },
      {
        icon: "Users",
        title: "Local & Family-Owned",
        body: "Your neighbors serving neighbors with integrity and care.",
      },
      {
        icon: "MapPin",
        title: "One-Hour Service Radius",
        body: "Quick response times across Southern York County, PA.",
      },
      {
        icon: "Hammer",
        title: "High-Quality Craftsmanship",
        body: "Expert work using premium materials and proven techniques.",
      },
      {
        icon: "Sparkle",
        title: "Premium Materials",
        body: "We source only the best products for lasting results.",
      },
    ],
    services_overline: "What we do",
    services_title: "Popular services",
    services_view_all: "View all services",
    specialties_overline: "Full Capabilities",
    specialties_title_line_1: "Everything",
    specialties_title_line_2: "under one roof.",
    specialties_body:
      "From design and structural work down to the final coat of paint — one trusted crew. No subbing the job out, no finger-pointing.",
    specialties_footnote:
      "Don't see what you need? Just ask — we probably do that too.",
    testimonials_overline: "Word of mouth",
    testimonials_title_line_1: "Neighbors",
    testimonials_title_line_2: "who stayed loyal.",
    areas_overline: "Service area",
    areas_title_line_1: "Proudly",
    areas_title_line_2: "local.",
    areas_body:
      "One-hour radius. Don't see your town? Ask — we probably still cover it.",
  },
  services_page: {
    hero_overline: "Our services",
    hero_title_line_1: "Inside. Outside.",
    hero_title_line_2: "Everywhere.",
    hero_body:
      "From custom decks to full home additions — one team, one warranty, one standard of craft.",
  },
  gallery_page: {
    hero_overline: "Gallery",
    hero_title_line_1: "The work.",
    hero_title_line_2: "The proof.",
    hero_body:
      "A growing archive of ATC Home Pro's projects across Southern York County. Updated weekly.",
    empty_message: "No projects in this category yet.",
  },
  blog_page: {
    hero_overline: "Journal",
    hero_title_line_1: "Project notes",
    hero_title_line_2: "from the field.",
    hero_body: "Build logs, material deep-dives, and updates from ongoing jobs.",
    empty_message: "No posts yet — check back soon.",
  },
  contact_page: {
    hero_overline: "About & Contact",
    hero_title_line_1: "Let's build",
    hero_title_line_2: "something good.",
    story_overline: "Our story",
    story_title_line_1: "Family-owned.",
    story_title_line_2: "Locally loyal.",
    story_body_1:
      "ATC Home Pro's was founded on a simple idea: deliver home improvement work homeowners can actually trust. We're family-owned and serving York County and Northern Maryland.",
    story_body_2:
      "Every team member shares our core values — integrity, professionalism, and respect for your home. That's why we back every project with a lifetime labor warranty.",
    warranty_title: "Lifetime labor warranty",
    warranty_body:
      "Quality that lasts — guaranteed on every ATC Home Pro's project.",
    form_overline: "Get in touch",
    form_title_line_1: "Send us a",
    form_title_line_2: "message.",
    form_submit_label: "Send Message",
    form_success_toast: "Thanks! We'll reach out within one business day.",
    info_overline: "Direct line",
    areas_overline: "Service area",
  },
  service_detail: {
    back_label: "All Services",
    overview_overline: "Overview",
    overview_title_line_1: "Built right.",
    overview_title_line_2: "Warrantied for life.",
    features_overline: "What's included",
    features_title: "The full scope.",
    ba_overline: "Recent projects",
    ba_title_line_1: "Before",
    ba_title_line_2: "& after.",
    warranty_overline: "Our promise",
    warranty_title: "Lifetime warranty on labor",
    warranty_body_template:
      "Every {service} project is backed by our lifetime warranty on labor. Quality that lasts — guaranteed.",
  },
};

/**
 * Fallback site settings so Home/Contact/Header render with the right
 * address, phone, specialties, and service areas even when /api/site
 * is unreachable. Live admin edits still win.
 */
export const DEFAULT_SITE = {
  phone: "410-800-7259",
  email: "info@atchomepros.com",
  address: "Glen Rock, PA",
  showroom_address: "Loganville, PA",
  hours_mon_fri: "8:00 AM – 6:00 PM",
  hours_sat: "By Appointment",
  hours_sun: "Closed",
  tagline: "When Quality Counts… Count On Us!",
  business_name: "ATC Home Pro's",
  legal_name: "ATC Home Pro's Construction",
  site_url: "https://atchomepros.com",
  service_radius_miles: 60,
  founding_year: 1991,
  city: "Glen Rock",
  region: "PA",
  country: "US",
  service_areas: [
    "Glen Rock PA",
    "Shrewsbury PA",
    "New Freedom PA",
    "York PA",
    "Hanover PA",
    "Spring Grove PA",
    "Stewartstown PA",
    "Loganville PA",
    "Seven Valleys PA",
    "Dallastown PA",
    "Southern York County PA",
    "Northern Maryland",
  ],
  specialties: [
    "Kitchen & Bathroom Design + Build",
    "Hardwood, Vinyl, Ceramic & Lifeproof Floors",
    "Custom Deck Design & Install",
    "Deck Tune-Ups",
    "New Doors & Windows",
    "Door & Window Tune-Ups",
    "Drywall",
    "Painting",
    "Trim",
    "Custom Design & Build",
    "Cabinets",
    "New Countertops",
    "Concrete Patios, Sidewalks, Porches & Driveways",
    "Stamped Concrete",
    "Plumbing & Electrical",
    "Siding & Roofs",
  ],
};

/**
 * Default services list — used as fallback render data when /api/services
 * is slow / fails. Each entry mirrors the public service contract.
 * IDs are placeholder strings (clients only need them as React keys).
 */
export const DEFAULT_SERVICES = [
  {
    id: "default-custom-decks",
    slug: "custom-decks",
    title: "Custom Decks",
    short_description: "Beautiful outdoor living spaces built to last with premium materials.",
    image_path: DECK_IMG,
    order: 1,
  },
  {
    id: "default-custom-kitchens",
    slug: "custom-kitchens",
    title: "Custom Kitchens",
    short_description: "Transform your kitchen into the heart of your home.",
    image_path: HERO_IMG,
    order: 2,
  },
  {
    id: "default-bathroom-remodeling",
    slug: "bathroom-remodeling",
    title: "Bathroom Remodeling",
    short_description: "Luxurious bathroom renovations with modern fixtures and designs.",
    image_path: SERVICE_IMG["bathroom-remodeling"],
    order: 3,
  },
  {
    id: "default-roofing",
    slug: "roofing",
    title: "Lifetime Roofs",
    short_description: "Durable roofing solutions backed by lifetime warranty.",
    image_path: SERVICE_IMG.roofing,
    order: 4,
  },
  {
    id: "default-siding-gutters",
    slug: "siding-gutters",
    title: "Siding & Gutters",
    short_description: "Protect and beautify your home's exterior.",
    image_path: SERVICE_IMG["siding-gutters"],
    order: 5,
  },
  {
    id: "default-additions-remodels",
    slug: "additions-remodels",
    title: "Additions & Remodels",
    short_description: "Add square footage or reimagine what you already have.",
    image_path: SERVICE_IMG["additions-remodels"],
    order: 6,
  },
];


/** Deep-merge override onto base. Override leaves are preferred when non-empty. */
export function deepMerge(base, override) {
  if (!override || typeof override !== "object") return base;
  if (Array.isArray(override)) {
    // For arrays: prefer override if non-empty; otherwise keep base
    return override.length > 0 ? override : base;
  }
  const out = { ...base };
  for (const k of Object.keys(override)) {
    const ov = override[k];
    const bv = base?.[k];
    if (
      ov &&
      typeof ov === "object" &&
      !Array.isArray(ov) &&
      bv &&
      typeof bv === "object" &&
      !Array.isArray(bv)
    ) {
      out[k] = deepMerge(bv, ov);
    } else if (ov === "" || ov === null || ov === undefined) {
      // Skip empty overrides — keep the default
      out[k] = bv;
    } else if (Array.isArray(ov)) {
      out[k] = ov.length > 0 ? ov : bv;
    } else {
      out[k] = ov;
    }
  }
  return out;
}
